import buildPath from 'marketing-site/lib/buildPath'
import { IPageType } from 'marketing-site/lib/content/pageTypes'
import PageBehaviors from 'marketing-site/lib/page-behaviors/PageBehaviors'
import { captureException } from 'marketing-site/lib/sentry'
import FaviconLinks from 'marketing-site/src/components/common/FaviconLinks'
import LocaleLinks from 'marketing-site/src/components/common/LocaleLinks'
import SEOMetadata, { ISeoProps } from 'marketing-site/src/components/common/SEOMetadata'
import CurrentPathContext from 'marketing-site/src/components/context/CurrentPathContext'
import EditingContext from 'marketing-site/src/components/context/EditingContext'
import useLocaleCode from 'marketing-site/src/library/hooks/useLocaleCode'
import { BrandOptions, BrandProvider } from 'marketing-site/src/library/providers/brandProvider'
import { ToggleProvider } from 'marketing-site/src/library/providers/toggleProvider'
import { ContentfulLayout } from 'marketing-site/src/transformers/components/ContentfulLayout'
import {
  isCapabilityPage,
  transformCapabilityPage,
} from 'marketing-site/src/transformers/pageTypes/ContentfulCapabilityPage'
import {
  isSolutionPage,
  transformSolutionPage,
} from 'marketing-site/src/transformers/pageTypes/ContentfulSolutionPage'
import {
  isProductPage,
  transformProductPage,
} from 'marketing-site/src/transformers/pageTypes/ContentfulProductPage'
import { transformSeoMetadata } from 'marketing-site/src/transformers/SEOMetadata'
import Head from 'next/head'
import { useRouter } from 'next/router'
import React from 'react'
import { ChatWithUsProvider } from '../context/ChatWithUsContext'
import { IntersectionProvider } from '../context/IntersectionContext'
import { MediaProvider } from '../context/MediaContext'
import GtmTrackingInitializer from './GtmTrackingInitializer'
import { CapabilityPage } from './pages/CapabilityPage'
import { GenericPage, isGenericPage } from './pages/GenericPage'
import { SolutionPage } from './pages/SolutionPage'
import { ProductPage } from './pages/ProductPage'

interface IProps {
  page: IPageType
  seo?: ISeoProps
  title?: string
  children?: React.ReactNode
}

export function Page({ page, children, seo, title }: IProps): React.ReactElement {
  const { fields } = page
  const localeCode = useLocaleCode()
  const router = useRouter()

  const showEditing = process.env.EDITOR_UI === 'true' && !!router.query.editing

  const brand = BrandOptions.default

  const renderPageContent = (page: IPageType, children?: React.ReactNode) => {
    if (children) {
      return children
    } else if (isGenericPage(page)) {
      return <GenericPage {...page} />
    } else if (isSolutionPage(page)) {
      return <SolutionPage {...transformSolutionPage(page)} />
    } else if (isCapabilityPage(page)) {
      return <CapabilityPage {...transformCapabilityPage(page)} />
    } else if (isProductPage(page)) {
      return <ProductPage {...transformProductPage(page)} />
    } else {
      captureException(new Error('Tried to render an unknown page type'))
    }
  }

  const pageBehaviors = ('behaviors' in fields && fields.behaviors) || []

  return (
    <BrandProvider forcedBrand={brand}>
      <ToggleProvider brand={brand}>
        <CurrentPathContext.Provider
          value={buildPath({ localeCode, pathname: fields.path, preserveQuery: true })}
        >
          <EditingContext.Provider value={showEditing}>
            <ChatWithUsProvider>
              <GtmTrackingInitializer />
              <MediaProvider>
                <IntersectionProvider>
                  <PageBehaviors pageBehaviors={pageBehaviors}>
                    <ContentfulLayout layout={fields.layout} pageBehaviors={pageBehaviors}>
                      <Head>
                        <title>{title || fields.title}</title>
                        <meta name="viewport" content="initial-scale=1, width=device-width" />
                      </Head>
                      <FaviconLinks />
                      <LocaleLinks />
                      <SEOMetadata {...transformSeoMetadata(fields.seoMetadata)} {...seo} />
                      {renderPageContent(page, children)}
                    </ContentfulLayout>
                  </PageBehaviors>
                </IntersectionProvider>
              </MediaProvider>
            </ChatWithUsProvider>
          </EditingContext.Provider>
        </CurrentPathContext.Provider>
      </ToggleProvider>
    </BrandProvider>
  )
}

export default Page
